import React from 'react';
import { FontAwesome6 } from '@expo/vector-icons';
import {
    Platform,
    Pressable,
    StyleSheet,
    Text,
    useWindowDimensions,
    View,
} from 'react-native';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interRegular, interSemiBold } from '../../lib/fonts';
import {
    formatForwardedImportCount,
    formatImportedPaperCount,
    type ForwardedPreviewItem,
} from '../../lib/forwardedOnboarding';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useResolvedMessage } from '../../lib/i18n/messages';
import type { LocalizedMessage } from '../../lib/i18n/messages';
import { useLingui } from '@lingui/react';
import { resolveMessage } from '../../lib/i18n/messages';

const STEP_ITEMS = [
    { label: msg({ id: 'forwardedStepper.sourceConnected', message: 'Source connected' }), status: 'done' as const },
    { label: msg({ id: 'forwardedStepper.setUpTopic', message: 'Set up research topic' }), status: 'current' as const },
    { label: msg({ id: 'forwardedStepper.focusedFeed', message: 'Get focused feed' }), status: 'upcoming' as const },
];
const HIDE_STEPPER_BREAKPOINT = 640;

export function ForwardedOnboardingStepper() {
    const { fontsLoaded } = useFontLoading();
    const { width } = useWindowDimensions();

    if (width <= HIDE_STEPPER_BREAKPOINT) {
        return null;
    }

    return (
        <View style={styles.stepper}>
            {STEP_ITEMS.map((step, index) => (
                <React.Fragment key={step.label.id}>
                    <View
                        style={[
                            styles.stepPill,
                            step.status === 'done' && styles.stepPillDone,
                            step.status === 'current' && styles.stepPillCurrent,
                        ]}
                    >
                        {step.status === 'done' ? (
                            <FontAwesome6
                                name="circle-check"
                                size={12}
                                color={colors.primaryGreenDark}
                            />
                        ) : (
                            <View
                                style={[
                                    styles.stepDot,
                                    step.status === 'current' && styles.stepDotCurrent,
                                ]}
                            />
                        )}
                        <Text
                            style={[
                                styles.stepLabel,
                                step.status === 'done' && styles.stepLabelDone,
                                step.status === 'current' && styles.stepLabelCurrent,
                                fontsLoaded && interSemiBold,
                            ]}
                        >
                            <StepLabel message={step.label} />
                        </Text>
                    </View>
                    {index < STEP_ITEMS.length - 1 ? <View style={styles.stepConnector} /> : null}
                </React.Fragment>
            ))}
        </View>
    );
}

function StepLabel({ message }: { message: (typeof STEP_ITEMS)[number]['label'] }) {
    return useResolvedMessage(message);
}

export function ImportedPaperPreview({
    previewItems,
    fallbackMessage,
    importedPaperCount,
}: {
    previewItems: ForwardedPreviewItem[];
    fallbackMessage: LocalizedMessage;
    importedPaperCount: number;
}) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const resolvedFallbackMessage = useResolvedMessage(fallbackMessage);

    return (
        <View style={styles.previewCard}>
            <View style={styles.previewHeader}>
                <View style={styles.previewTitleRow}>
                    <Text style={[styles.previewTitle, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedonboardingshared.importedPapers" comment="UI copy in components/onboarding/ForwardedOnboardingShared.tsx">
                        Imported papers
                    </Trans></Text>
                    {importedPaperCount > 0 ? (
                        <View style={styles.previewCountBadge}>
                            <Text style={[styles.previewCountText, fontsLoaded && interSemiBold]}>
                                {importedPaperCount}
                            </Text>
                        </View>
                    ) : null}
                </View>
            </View>

            {previewItems.length > 0 ? (
                <View style={styles.previewList}>
                    {previewItems.map((item) => (
                        <View key={item.id} style={styles.previewRow}>
                            <View style={styles.previewRowBullet} />
                            <View style={styles.previewRowBody}>
                                <Text
                                    style={[styles.previewRowTitle, fontsLoaded && interRegular]}
                                    numberOfLines={2}
                                >
                                    {item.title}
                                </Text>
                                <Text style={[styles.previewRowMeta, fontsLoaded && interRegular]}>
                                    {resolveMessage(i18n, item.senderLabel)}
                                </Text>
                            </View>
                        </View>
                    ))}
                </View>
            ) : (
                <View style={styles.previewFallback}>
                    <FontAwesome6 name="circle-info" size={14} color={colors.primaryGreenDark} />
                    <Text style={[styles.previewFallbackText, fontsLoaded && interRegular]}>
                        {resolvedFallbackMessage}
                    </Text>
                </View>
            )}
        </View>
    );
}

export function ForwardedImportContextCard({
    importedPaperCount,
    importCount,
}: {
    importedPaperCount: number;
    importCount: number;
}) {
    const { fontsLoaded } = useFontLoading();
    const importedCountLabel = useResolvedMessage(formatImportedPaperCount(importedPaperCount));
    const forwardedCountLabel = useResolvedMessage(formatForwardedImportCount(importCount));

    return (
        <View style={styles.contextCard} testID="forwarded-import-context-card">
            <Text style={[styles.contextTitle, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedonboardingshared.importedFromYourAlert" comment="UI copy in components/onboarding/ForwardedOnboardingShared.tsx">
                Imported from your alert
            </Trans></Text>
            <View style={styles.contextLine}>
                <FontAwesome6 name="file-import" size={12} color={colors.primaryGreenDark} />
                <Text style={[styles.contextText, fontsLoaded && interSemiBold]}>
                    {importedCountLabel}
                </Text>
            </View>
            <View style={styles.contextLine}>
                <FontAwesome6 name="bullseye" size={12} color={colors.primaryGreenDark} />
                <Text style={[styles.contextText, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedonboardingshared.importedPapersWillBeAddedToYourTopic" comment="UI copy in components/onboarding/ForwardedOnboardingShared.tsx">
                    Imported papers will be added to your topic
                </Trans></Text>
            </View>
            <View style={styles.contextLine}>
                <FontAwesome6 name="inbox" size={12} color={colors.primaryGreenDark} />
                <Text style={[styles.contextText, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedonboardingshared.originalEmailsStayVisibleInInbox" comment="UI copy in components/onboarding/ForwardedOnboardingShared.tsx">
                    Original emails stay visible in Inbox
                </Trans></Text>
            </View>
            <Text style={[styles.contextMeta, fontsLoaded && interRegular]}>
                {forwardedCountLabel}
            </Text>
        </View>
    );
}

export function ForwardedStatPills({
    importCount,
    importedPaperCount,
}: {
    importCount: number;
    importedPaperCount: number;
}) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const items = [
        formatImportedPaperCount(importedPaperCount),
        formatForwardedImportCount(importCount),
    ].map((message) => resolveMessage(i18n, message));

    return (
        <View style={styles.statPills}>
            {items.map((item) => (
                <View key={item} style={styles.statPill}>
                    <Text style={[styles.statPillText, fontsLoaded && interSemiBold]}>{item}</Text>
                </View>
            ))}
        </View>
    );
}

export function InlineLinkButton({
    label,
    onPress,
}: {
    label: string;
    onPress: () => void;
}) {
    const { fontsLoaded } = useFontLoading();

    return (
        <Pressable onPress={onPress} style={styles.inlineLinkButton}>
            <Text style={[styles.inlineLinkText, fontsLoaded && interSemiBold]}>{label}</Text>
        </Pressable>
    );
}

const styles = StyleSheet.create({
    stepper: {
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: 10,
    },
    stepPill: {
        alignItems: 'center',
        backgroundColor: '#f3f4f6',
        borderColor: '#e5e7eb',
        borderRadius: 999,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    stepPillDone: {
        backgroundColor: '#edf9f0',
        borderColor: '#cfe9d7',
    },
    stepPillCurrent: {
        backgroundColor: '#10261b',
        borderColor: '#10261b',
    },
    stepDot: {
        backgroundColor: '#94a3b8',
        borderRadius: 999,
        height: 8,
        width: 8,
    },
    stepDotCurrent: {
        backgroundColor: '#86efac',
    },
    stepLabel: {
        ...fonts.label,
        color: colors.text700,
    },
    stepLabelDone: {
        color: colors.primaryGreenDark,
    },
    stepLabelCurrent: {
        color: '#ffffff',
    },
    stepConnector: {
        backgroundColor: '#d6dae1',
        height: 1,
        minWidth: 18,
    },
    previewCard: {
        backgroundColor: '#fbfcfb',
        borderColor: '#e4e9e3',
        borderRadius: 20,
        borderWidth: 1,
        gap: 12,
        paddingHorizontal: 18,
        paddingVertical: 16,
    },
    previewHeader: {
        gap: 4,
    },
    previewTitleRow: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 8,
    },
    previewTitle: {
        ...fonts.bodyMedium,
        color: colors.text900,
    },
    previewCountBadge: {
        backgroundColor: '#eef2ee',
        borderColor: '#d6ddd6',
        borderRadius: 999,
        borderWidth: 1,
        paddingHorizontal: 8,
        paddingVertical: 2,
    },
    previewCountText: {
        ...fonts.label,
        color: colors.text600,
    },
    previewList: {
        gap: 10,
    },
    previewRow: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        gap: 10,
    },
    previewRowBullet: {
        backgroundColor: '#9bb89f',
        borderRadius: 999,
        height: 6,
        marginTop: 7,
        width: 6,
    },
    previewRowBody: {
        flex: 1,
        gap: 2,
    },
    previewRowTitle: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.text900,
    },
    previewRowMeta: {
        fontSize: 12,
        lineHeight: 17,
        color: colors.text500,
    },
    previewFallback: {
        alignItems: 'flex-start',
        backgroundColor: '#f5faf5',
        borderColor: '#d5e7d5',
        borderRadius: 18,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 10,
        padding: 14,
    },
    previewFallbackText: {
        ...fonts.bodySmall,
        color: colors.text700,
        flex: 1,
    },
    contextCard: {
        backgroundColor: '#f7faf7',
        borderColor: '#d8e2d6',
        borderRadius: 22,
        borderWidth: 1,
        gap: 10,
        padding: 18,
    },
    contextTitle: {
        ...fonts.bodyMedium,
        color: colors.text900,
    },
    contextLine: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 10,
    },
    contextText: {
        ...fonts.bodySmall,
        color: colors.text900,
    },
    contextMeta: {
        ...fonts.bodySmall,
        color: colors.text500,
        marginTop: 2,
    },
    statPills: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    statPill: {
        backgroundColor: '#eef7ee',
        borderColor: '#d7e7d8',
        borderRadius: 999,
        borderWidth: 1,
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    statPillText: {
        ...fonts.bodySmall,
        color: colors.primaryGreenDark,
    },
    inlineLinkButton: {
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    inlineLinkText: {
        ...fonts.bodySmall,
        color: colors.primaryGreenDark,
        textDecorationLine: 'underline',
    },
});
